import { useCallback, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type ApiError,
	mapErrorToApiError,
} from '@atlassian/conversation-assistant-service-api/utils/api-error';

type MutateFunction<TPayload, TResponse> = (
	payload: TPayload,
	callbacks?: {
		onSuccess?: (data: TResponse) => void;
		onError?: (error: ApiError) => void;
	},
) => Promise<TResponse | undefined>;

type UseMutationProps<TPayload, TResponse> = {
	mutationFn: (payload: TPayload) => Promise<TResponse>;
	onSuccess?: (data: TResponse) => void | Promise<void>;
	onError?: (error: ApiError, payload: TPayload) => void;
	/** With rovo_ai_mate_reliability_contracts enabled, reject request failures after onError. */
	throwOnError?: boolean;
};

export const useMutation = <TPayload, TResponse = void>({
	mutationFn,
	onSuccess,
	onError,
	throwOnError = false,
}: UseMutationProps<TPayload, TResponse>): {
	data: TResponse | null;
	error: ApiError | null;
	isSubmitting: boolean;
	mutate: MutateFunction<TPayload, TResponse>;
} => {
	const mutationFnRef = useRef(mutationFn);
	mutationFnRef.current = mutationFn;

	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	const throwOnErrorRef = useRef(throwOnError);
	throwOnErrorRef.current = throwOnError;

	const [data, setData] = useState<TResponse | null>(null);
	const [error, setError] = useState<ApiError | null>(null);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const mutate: MutateFunction<TPayload, TResponse> = useCallback(
		async (payload: TPayload, mutateCallback) => {
			const reliableContracts = fg('rovo_ai_mate_reliability_contracts');
			const shouldThrow = reliableContracts && throwOnErrorRef.current;
			const handleSuccess = onSuccessRef.current;
			const handleError = onErrorRef.current;
			setIsSubmitting(true);
			if (reliableContracts) {
				setError(null);
			}
			try {
				const response = await mutationFnRef.current(payload);
				setData(response);
				if (reliableContracts) {
					// The write has succeeded. An observer failure must not trigger rollback or
					// encourage callers to repeat an already committed operation.
					for (const callback of [handleSuccess, mutateCallback?.onSuccess]) {
						try {
							await callback?.(response);
						} catch (callbackError) {
							void logException({
								exception: callbackError,
								name: 'aiMateMutationSuccessCallback',
							}).catch(() => undefined);
						}
					}
				} else {
					onSuccessRef.current?.(response);
					mutateCallback?.onSuccess?.(response);
				}
				return response;
			} catch (error) {
				const apiError = mapErrorToApiError(error);
				setError(apiError);
				(reliableContracts ? handleError : onErrorRef.current)?.(apiError, payload);
				mutateCallback?.onError?.(apiError);
				if (shouldThrow) {
					throw apiError;
				}
			} finally {
				setIsSubmitting(false);
			}
		},
		[],
	);

	return { data, error, isSubmitting, mutate };
};
